import { SectionHeader, SectionHeaderProps } from '@/components/ui/section-header';
import { PluginConfigData } from '@/types/plugin-config';
import {
  ApplePayButton,
  StripeExpressButton,
  CheckoutData,
  FunnelActionType,
  GooglePayButton,
  useExpressPaymentMethods,
  useFunnel,
  usePluginConfig,
  useTranslation,
} from '@tagadapay/plugin-sdk/v2';
import { toast } from 'react-hot-toast';

type ExpressCheckoutButtonProps = {
  checkout: CheckoutData;
};

function ExpressCheckoutButtons({ checkout }: ExpressCheckoutButtonProps) {
  const { t } = useTranslation();
  const {
    applePayPaymentMethod,
    googlePayPaymentMethod,
    stripeExpressPaymentMethod,
    availableExpressPaymentMethodIds,
    loading,
    error,
  } = useExpressPaymentMethods();
  const { config } = usePluginConfig<PluginConfigData>();
  const { next } = useFunnel();
  const sectionHeaderProps: SectionHeaderProps = {
    title: t(config?.checkout?.expressCheckout?.title, 'Express checkout'),
    description: t(
      config?.checkout?.expressCheckout?.description,
      'Skip the form and checkout faster with one of these options.',
    ),
    descriptionEditorId: 'config.checkout.expressCheckout.description',
    titleEditorId: 'config.checkout.expressCheckout.title',
    spacing: 'compact',
  };

  // Native Apple/Google Pay paths render reliably when their config flags are present.
  // Stripe Express Checkout Element only renders if the browser/device actually exposes
  // a wallet — we learn that via `handleAddExpressId('stripe_express_checkout')` fired
  // from the SDK's onReady. Trust that signal rather than the config-level flag.
  const stripeExpressReady = availableExpressPaymentMethodIds.includes('stripe_express_checkout');
  const hasVisibleButton = !!applePayPaymentMethod || !!googlePayPaymentMethod || stripeExpressReady;

  // Initial loading: render skeleton buttons sized like the real ones (h-12).
  const showInitialLoading = loading && !applePayPaymentMethod && !googlePayPaymentMethod && !stripeExpressPaymentMethod;
  if (showInitialLoading) {
    return (
      <div className="space-y-4">
        <SectionHeader {...sectionHeaderProps} />
        <div className="space-y-3">
          <div className="h-12 animate-pulse rounded-lg bg-gray-200" />
          <div className="h-12 animate-pulse rounded-lg bg-gray-200" />
        </div>
      </div>
    );
  }

  if (error) {
    console.error('Express payment methods error:', error);
    return null;
  }

  // Nothing configured at all — nothing to render or probe.
  const hasAnyConfigured = applePayPaymentMethod || googlePayPaymentMethod || stripeExpressPaymentMethod;
  if (!hasAnyConfigured) {
    return null;
  }

  // Shared success/error/cancel handlers — used by all express payment buttons.
  // The `method` is passed by the calling button so funnel-edge conditions
  // (e.g. "route to apple-pay-specific thank-you") can match correctly.
  const handleSuccess = async (
    result: {
      payment: { id: string; status: string };
      order: { id: string; amount: number; currency: string };
    },
    method: 'apple_pay' | 'google_pay' | 'stripe_express',
  ) => {
    await next({
      type: FunnelActionType.PAYMENT_SUCCESS,
      data: {
        paymentId: result?.payment?.id,
        payment: {
          id: result?.payment?.id,
          status: result?.payment?.status,
          method,
        },
        order: result?.order
          ? {
            id: result.order.id,
            amount: result.order.amount,
            currency: result.order.currency,
          }
          : undefined,
        resources: {
          order: result?.order
            ? {
              id: result.order.id,
              amount: result.order.amount,
              currency: result.order.currency,
            }
            : undefined,
          mainOrder: result?.order
            ? {
              id: result.order.id,
              amount: result.order.amount,
              currency: result.order.currency,
            }
            : undefined,
          payment: {
            id: result?.payment?.id,
            status: result?.payment?.status,
            method,
          },
          checkout: {
            id: checkout.checkoutSession.id,
            token: checkout.checkoutSession.checkoutToken,
            selectedPresentmentCurrency: checkout.checkoutSession.selectedPresentmentCurrency,
          },
        },
      },
    });
  };

  const handleError = async (error: string) => {
    toast.error(t(config?.checkout?.errors?.paymentFailed, error || 'Payment failed'));
    await next({
      type: FunnelActionType.PAYMENT_FAILED,
      data: { error: { message: error } },
    });
  };

  const handleCancel = () => {
    console.log('Express payment cancelled by user');
  };

  // When only Stripe Express is configured and it's still probing (or has no
  // available wallet on this device), pull the whole section out of flow so the
  // header/divider don't render an empty shell. The StripeExpressButton stays
  // mounted in its stable React position so its iframe/Stripe Elements instance
  // is preserved when it later becomes ready.
  // Off-screen but full-size so the Stripe ECE iframe still gets layout and can
  // fire onReady. Zero-size containers can prevent the iframe from probing.
  const offFlowStyle = {
    position: 'absolute' as const,
    left: '-9999px',
    top: '-9999px',
    pointerEvents: 'none' as const,
  };

  return (
    <div
      className={hasVisibleButton ? 'space-y-4' : undefined}
      style={hasVisibleButton ? undefined : offFlowStyle}
      aria-hidden={hasVisibleButton ? undefined : 'true'}
      editor-id="config.checkoutSettings.showExpressCheckout"
    >
      {hasVisibleButton && <SectionHeader {...sectionHeaderProps} />}
      <div className="space-y-3">
        {/* Apple Pay (Basis Theory / native ApplePaySession) */}
        {applePayPaymentMethod && (
          <ApplePayButton
            checkout={checkout}
            onSuccess={async (result) => {
              console.log('Apple Pay payment successful:', result);
              await handleSuccess(result as any, 'apple_pay');
            }}
            onError={async (error) => {
              console.error('Apple Pay payment failed:', error);
              await handleError(error);
            }}
            onCancel={handleCancel}
          />
        )}

        {/* Apple Pay + Google Pay via Stripe Express Checkout Element */}
        <StripeExpressButton
          checkout={checkout}
          onSuccess={async (result) => {
            console.log('Stripe Express Checkout payment successful:', result);
            await handleSuccess(result as any, 'stripe_express');
          }}
          onError={async (error) => {
            console.error('Stripe Express Checkout payment failed:', error);
            await handleError(error);
          }}
          onCancel={handleCancel}
        />

        {/* Google Pay (Basis Theory / native) */}
        {googlePayPaymentMethod && (
          <GooglePayButton
            checkout={checkout}
            onSuccess={async (result) => {
              console.log('Google Pay payment successful:', result);
              await handleSuccess(result as any, 'google_pay');
            }}
            onError={async (error) => {
              console.error('Google Pay payment failed:', error);
              await handleError(error);
            }}
            onCancel={handleCancel}
          />
        )}
      </div>

      {/* Divider */}
      {hasVisibleButton && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-[var(--border-color)]" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span
              className="text-[var(--text-secondary-color)] bg-[var(--background-color)] px-2"
              editor-id="config.checkout.expressCheckout.dividerText"
            >
              {t(config?.checkout?.expressCheckout?.dividerText, 'Or')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpressCheckoutButtons;

